// ============================================================================
// JARVISTRAVEL - EARLY ACCESS SIGNUP GATE (JAR-34)
// ============================================================================

import { MARKETING_URLS } from '@/app/constants';

/**
 * Whether public self-service signup is open.
 *
 * Fails closed, mirroring the backend gate (`handlers.SelfSignupEnabled()` in
 * core): only an unambiguous "true" opens signup, so a missing or mistyped
 * `VITE_SELF_SIGNUP_ENABLED` cannot accidentally reopen registration during
 * Early Access. Casing and stray whitespace are tolerated as operator
 * formatting; "1" and "yes" are not accepted.
 *
 * This is presentation only — it decides whether we show the signup form or the
 * Early Access notice. It is not a security control: the backend refuses
 * `POST /v1/auth/register` with `403 SIGNUP_DISABLED` no matter what the client
 * believes, and Clerk's own sign-up mode is a third, independent gate.
 *
 * Reopening signup at GA takes all three: this flag, `SELF_SIGNUP_ENABLED` in
 * core, and Clerk dashboard → Restrictions → Sign-up mode: Public.
 */
export function isSelfSignupEnabled(): boolean {
  return String(import.meta.env.VITE_SELF_SIGNUP_ENABLED ?? '')
    .trim()
    .toLowerCase() === 'true';
}

/**
 * Where prospective users go while signup is closed.
 *
 * This is the live waitlist capture (`POST /api/waitlist`) — the same
 * destination the marketing site's Contact page links to for "Join Now"
 * (marketing-website `ContactPage.tsx`, JAR-432, verified there). Kept
 * byte-identical to that link so the two properties don't drift, and so a
 * redirect hop isn't added between the app and the capture form.
 */
export const WAITLIST_URL = MARKETING_URLS.home;
